import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "@/utils/apiConfig";
import { useRestaurantMenuLinkBase } from "@/components/menu/ProductShareLink";
import ProductDetailsForm from "@/components/menu/ProductDetailsForm";
import ProductAgendaTab from "@/components/menu/ProductAgendaTab";
import ProductModifiersTab from "@/components/menu/ProductModifiersTab";

export function ProductFormDialog({ product, categories, onClose, onCategoriesRefresh }) {
  const { authAxios, user } = useAuth();
  const menuLinkBase = useRestaurantMenuLinkBase(authAxios, user?.restaurant_id);
  const authAxiosRef = React.useRef(authAxios); authAxiosRef.current = authAxios;
  const { allowVideo } = useSubscription();
  const [activeTab, setActiveTab] = useState("detalles");
  const [name, setName] = useState(product?.name || "");
  const [nameEn, setNameEn] = useState(product?.translations?.en || "");
  const [nameEs, setNameEs] = useState(product?.translations?.es || "");
  const [price, setPrice] = useState(product?.price || "");
  const [originalPrice, setOriginalPrice] = useState(product?.original_price || "");
  const [discountPct, setDiscountPct] = useState(product?.discount_percentage || "");
  const [code, setCode] = useState(product?.code || "");
  const [categoryId, setCategoryId] = useState(product?.category_id || categories[0]?.id || "");
  const [imageUrl, setImageUrl] = useState(product?.image_url || "");
  const [videoUrl, setVideoUrl] = useState(product?.video_url || "");
  const [mediaType, setMediaType] = useState(product?.media_type || "image");
  const [description, setDescription] = useState(product?.description || "");
  const [quantity, setQuantity] = useState(product?.quantity || 0);
  const [prepTime, setPrepTime] = useState(product?.preparation_time || 15);
  const [availability, setAvailability] = useState(product?.availability || "all");
  const [allergens, setAllergens] = useState(product?.allergens || []);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);
  const [position, setPosition] = useState(product?.position ?? 0);
  const [showInDelivery, setShowInDelivery] = useState(product?.show_in_delivery !== false);
  const [showInTable, setShowInTable] = useState(product?.show_in_table !== false);
  const [showInPos, setShowInPos] = useState(product?.show_in_pos !== false);
  const [priceTable, setPriceTable] = useState(product?.price_table || "");
  const [posMediaUrl, setPosMediaUrl] = useState(product?.pos_media_url || "");
  const [posMediaType, setPosMediaType] = useState(product?.pos_media_type || "image");
  const [uploadingPos, setUploadingPos] = useState(false);
  const posFileInputRef = React.useRef(null);
  const [scheduleEnabled, setScheduleEnabled] = useState(product?.schedule_enabled || false);
  const [scheduleSlots, setScheduleSlots] = useState(product?.schedule_slots || []);
  const [visibleWhenUnavailable, setVisibleWhenUnavailable] = useState(product?.visible_when_unavailable !== false);
  const [unavailableMessage, setUnavailableMessage] = useState(product?.unavailable_message || "");
  const [hideAfterExpiration, setHideAfterExpiration] = useState(product?.hide_after_expiration || false);
  const [publicDisplayOnly, setPublicDisplayOnly] = useState(product?.public_display_only || false);
  const [requiresBuffetRules, setRequiresBuffetRules] = useState(product?.requires_buffet_rules || false);
  const [showQuickCategoryModal, setShowQuickCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryPosition, setNewCategoryPosition] = useState(0);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [localCategories, setLocalCategories] = useState(categories);

  React.useEffect(() => { setLocalCategories(categories); }, [categories]);

  const handleQuickCreateCategory = async () => {
    if (!newCategoryName.trim()) { toast.error("Nombre es obligatorio"); return; }
    setCreatingCategory(true);
    try {
      const slug = newCategoryName.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
      const link = `${API_URL}/m/${user?.restaurant_id}/#/categoria/${slug}`;
      const res = await authAxios.post(`/menu/categories?name=${encodeURIComponent(newCategoryName.trim())}&position=${newCategoryPosition}&link=${encodeURIComponent(link)}`);
      const newCat = res.data;
      setLocalCategories(prev => [...prev, newCat]);
      setCategoryId(newCat.id);
      toast.success(`Categoria "${newCategoryName}" creada y seleccionada`);
      setShowQuickCategoryModal(false);
      setNewCategoryName("");
      setNewCategoryPosition(0);
      if (onCategoriesRefresh) onCategoriesRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al crear categoria"); }
    setCreatingCategory(false);
  };

  const handleMediaUpload = async (file) => {
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    if (!isImage && !isVideo) { toast.error("Solo imagenes o videos"); return; }
    if (isVideo && !allowVideo) { toast.error("Videos solo disponibles en el plan Premium (189 EUR/mes). Actualiza tu plan en Configuraciones."); return; }
    const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) { toast.error(`Archivo muy grande (max ${isVideo ? "50MB" : "5MB"})`); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await authAxios.post("/menu/upload-media", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      if (res.data.type === "video") { setVideoUrl(res.data.url); setImageUrl(""); setMediaType("video"); toast.success("Video subido!"); }
      else { setImageUrl(res.data.url); setVideoUrl(""); setMediaType("image"); toast.success("Imagen subida!"); }
    } catch (err) { toast.error(err.response?.data?.detail || "Error al subir"); }
    setUploading(false);
  };

  const handlePosMediaUpload = async (file) => {
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    if (!isImage && !isVideo) { toast.error("Solo imagenes o videos"); return; }
    const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) { toast.error(`Archivo muy grande (max ${isVideo ? "50MB" : "5MB"})`); return; }
    setUploadingPos(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await authAxios.post("/menu/upload-media", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      if (res.data.type === "video") { setPosMediaUrl(res.data.url); setPosMediaType("video"); toast.success("Video POS subido!"); }
      else { setPosMediaUrl(res.data.url); setPosMediaType("image"); toast.success("Imagen POS subida!"); }
    } catch (err) { toast.error(err.response?.data?.detail || "Error al subir"); }
    setUploadingPos(false);
  };

  const handleDrop = (e) => { e.preventDefault(); const file = e.dataTransfer?.files?.[0]; if (file) handleMediaUpload(file); };
  const toggleAllergen = (id) => setAllergens(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const translations = {};
      if (nameEn.trim()) translations.en = nameEn.trim();
      if (nameEs.trim()) translations.es = nameEs.trim();
      const data = {
        name, translations: Object.keys(translations).length > 0 ? translations : undefined,
        price: parseFloat(price), original_price: parseFloat(originalPrice) || 0,
        discount_percentage: parseFloat(discountPct) || 0, code, category_id: categoryId,
        image_url: imageUrl, video_url: videoUrl, media_type: mediaType, description,
        quantity: parseInt(quantity), preparation_time: parseInt(prepTime), availability, allergens,
        show_in_delivery: showInDelivery, show_in_table: showInTable, show_in_pos: showInPos,
        price_table: priceTable ? parseFloat(priceTable) : null,
        pos_media_url: posMediaUrl, pos_media_type: posMediaType, position: parseInt(position) || 0,
        schedule_enabled: scheduleEnabled, schedule_slots: scheduleSlots,
        visible_when_unavailable: visibleWhenUnavailable,
        unavailable_message: unavailableMessage, hide_after_expiration: hideAfterExpiration,
        public_display_only: publicDisplayOnly,
        requires_buffet_rules: requiresBuffetRules,
      };
      if (product?.id) {
        const res = await authAxios.put(`/menu/products/${product.id}`, data);
        toast.success("Actualizado");
        onClose(res.data || { ...product, ...data });
      } else {
        const res = await authAxios.post("/menu/products", data);
        toast.success("Creado");
        onClose(res.data);
      }
    } catch { toast.error("Error"); }
    setLoading(false);
  };

  const tabs = product?.id
    ? [{ id: "detalles", label: "Detalles" }, { id: "agenda", label: "Agenda" }, { id: "modificadores", label: "Modificadores" }]
    : [{ id: "detalles", label: "Detalles" }, { id: "agenda", label: "Agenda" }];

  return (
    <>
      <DialogHeader><DialogTitle className="uppercase font-bold">{product ? "Editar" : "Nuevo"} Producto</DialogTitle></DialogHeader>
      {tabs.length > 1 && (
        <div className="flex gap-1 border-b mb-2">
          {tabs.map(t => (
            <button key={t.id} type="button"
              className={`px-4 py-2 text-xs font-bold uppercase border-b-2 transition ${activeTab === t.id ? "border-emerald-600 text-emerald-700" : "border-transparent text-gray-400 hover:text-gray-600"}`}
              onClick={() => setActiveTab(t.id)}>{t.label}</button>
          ))}
        </div>
      )}
      <div className="overflow-y-auto">
        {activeTab === "detalles" && (
          <ProductDetailsForm
            name={name} setName={setName} nameEn={nameEn} setNameEn={setNameEn} nameEs={nameEs} setNameEs={setNameEs}
            price={price} setPrice={setPrice} originalPrice={originalPrice} setOriginalPrice={setOriginalPrice}
            discountPct={discountPct} setDiscountPct={setDiscountPct} code={code} setCode={setCode}
            categoryId={categoryId} setCategoryId={setCategoryId} localCategories={localCategories}
            setNewCategoryPosition={setNewCategoryPosition} setShowQuickCategoryModal={setShowQuickCategoryModal}
            imageUrl={imageUrl} setImageUrl={setImageUrl} videoUrl={videoUrl} setVideoUrl={setVideoUrl}
            mediaType={mediaType} setMediaType={setMediaType} description={description} setDescription={setDescription}
            quantity={quantity} setQuantity={setQuantity} prepTime={prepTime} setPrepTime={setPrepTime}
            availability={availability} setAvailability={setAvailability} allergens={allergens} toggleAllergen={toggleAllergen}
            uploading={uploading} uploadingPos={uploadingPos} position={position} setPosition={setPosition}
            showInDelivery={showInDelivery} setShowInDelivery={setShowInDelivery}
            showInTable={showInTable} setShowInTable={setShowInTable}
            showInPos={showInPos} setShowInPos={setShowInPos}
            priceTable={priceTable} setPriceTable={setPriceTable}
            posMediaUrl={posMediaUrl} setPosMediaUrl={setPosMediaUrl}
            posMediaType={posMediaType} setPosMediaType={setPosMediaType}
            publicDisplayOnly={publicDisplayOnly} setPublicDisplayOnly={setPublicDisplayOnly}
            requiresBuffetRules={requiresBuffetRules} setRequiresBuffetRules={setRequiresBuffetRules}
            loading={loading} handleSubmit={handleSubmit}
            handleMediaUpload={handleMediaUpload} handlePosMediaUpload={handlePosMediaUpload} handleDrop={handleDrop}
            fileInputRef={fileInputRef} posFileInputRef={posFileInputRef}
            allowVideo={allowVideo} product={product} menuLinkBase={menuLinkBase} onClose={onClose}
          />
        )}
        {activeTab === "agenda" && (
          <ProductAgendaTab
            scheduleEnabled={scheduleEnabled} setScheduleEnabled={setScheduleEnabled}
            scheduleSlots={scheduleSlots} setScheduleSlots={setScheduleSlots}
            visibleWhenUnavailable={visibleWhenUnavailable} setVisibleWhenUnavailable={setVisibleWhenUnavailable}
            unavailableMessage={unavailableMessage} setUnavailableMessage={setUnavailableMessage}
            hideAfterExpiration={hideAfterExpiration} setHideAfterExpiration={setHideAfterExpiration}
            loading={loading} handleSubmit={handleSubmit} onClose={onClose}
          />
        )}
        {activeTab === "modificadores" && product && (
          <ProductModifiersTab product={product} authAxios={authAxios} />
        )}
      </div>

      <Dialog open={showQuickCategoryModal} onOpenChange={setShowQuickCategoryModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-emerald-600" /> Nueva Categoria</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Nombre de la categoria *</Label>
              <Input value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)}
                placeholder="Ej: Entradas, Sushi, Postres" className="mt-1" autoFocus data-testid="quick-category-name" />
            </div>
            <div>
              <Label>Posicion en el menu</Label>
              <Input type="number" min={0} value={newCategoryPosition} onChange={e => setNewCategoryPosition(Number(e.target.value))} className="mt-1" data-testid="quick-category-position" />
              <p className="text-xs text-gray-400 mt-1">0 = primero, numeros mayores = mas abajo</p>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => setShowQuickCategoryModal(false)}>Cancelar</Button>
            <Button onClick={handleQuickCreateCategory} disabled={creatingCategory || !newCategoryName.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="quick-save-category-btn">
              {creatingCategory ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear y Seleccionar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
